import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { saveReport } from "../../utils/api";
import { clearProjectCache } from "utils/authUtils";
import "components/shared/occurrence-modal.css";
import AppModal from "components/ui/AppModal/AppModal";

const ReportCartModal = ({ onClose, folderPath, token }) => {
  const navigate = useNavigate();
  const email = useSelector(state => state.auth.email);
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    variant: "custom",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: null
  });

  useEffect(() => {
    const storedReports = JSON.parse(localStorage.getItem("reports")) || [];
    setReports(storedReports);
  }, []);

  const handleSave = async () => {
    if (reports.length === 0) {
      setFeedbackModal({
        open: true,
        variant: "warning",
        title: "Aviso",
        message: "Nenhum relatório para salvar.",
        confirmText: "OK",
        cancelText: null
      });
      return;
    }

    setIsLoading(true);
    setErrorMsg("");
    try {
      const result = await saveReport(reports, folderPath, token);

      if (result.success) {
        localStorage.removeItem("reports");
        clearProjectCache(email);
        setReports([]);
        setFeedbackModal({
          open: true,
          variant: "success",
          title: "Relatórios salvos!",
          message: `${result.savedCount} ocorrência${result.savedCount !== 1 ? "s" : ""} enviada${result.savedCount !== 1 ? "s" : ""} com sucesso.`,
          confirmText: "Ir para Relatórios",
          cancelText: "Fechar"
        });
      } else {
        setErrorMsg("Alguns relatórios falharam ao serem salvos.");
      }
    } catch (error) {
      setErrorMsg(error.message || "Erro ao tentar salvar os relatórios.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeFeedbackModal = () =>
    setFeedbackModal(prev => ({ ...prev, open: false }));

  const handleFeedbackConfirm = () => {
    closeFeedbackModal();
    // Parse folderPath: projetos/{name}/{date}/{product}
    const parts = (folderPath || "").split("/").filter(Boolean);
    const projectName = parts[1];
    const projectDate = parts[2];

    if (projectName && projectDate) {
      navigate("/reports/list", {
        state: {
          folder_path: `projetos/${projectName}/${projectDate}/relatorios/`,
          project: { name: projectName, type: "relatorios" },
          date: projectDate
        }
      });
    } else {
      navigate("/projects");
    }
  };

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      {createPortal(
        <div className="occ-list-overlay" onClick={handleBackdropClick}>
          <div className="occ-list">
            <div className="occ-list__header">
              <div className="occ-list__header-inner">
                <span className="occ-list__accent">Ocorrências</span>
                <h2 className="occ-list__title">Relatórios Pendentes</h2>
                <p className="occ-list__subtitle">
                  Revise os registros antes de enviar ao servidor.
                </p>
              </div>
              <button
                type="button"
                className="occ-list__close"
                onClick={onClose}
                aria-label="Fechar"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="occ-list__body">
              {reports.length === 0 ? (
                <div className="occ-list__empty">
                  <i className="fas fa-file-excel" />
                  <p>Nenhum relatório disponível.</p>
                </div>
              ) : (
                <div className="occ-list__items">
                  {reports.map((report, index) => (
                    <div key={index} className="occ-list-item">
                      <div
                        className="occ-list-item__img-wrap"
                        onClick={() => setExpandedImage(report.image)}
                      >
                        <img
                          src={report.image}
                          alt={`Snapshot ${index + 1}`}
                          className="occ-list-item__img"
                        />
                        <div className="occ-list-item__img-overlay">
                          <i className="fas fa-search-plus" />
                        </div>
                      </div>

                      <div className="occ-list-item__content">
                        <div className="occ-list-item__header">
                          <h6 className="occ-list-item__title">
                            {report.title || "Sem título"}
                          </h6>
                          {report.severity && (
                            <span
                              className="occ-list-item__severity"
                              data-sev={report.severity}
                            >
                              {report.severity === "alta"
                                ? "Alta"
                                : report.severity === "media"
                                ? "Média"
                                : "Baixa"}
                            </span>
                          )}
                        </div>
                        <div className="occ-list-item__details">
                          <p className="occ-list-item__desc">
                            <strong>Descrição:</strong>{" "}
                            {report.description || "Não informada"}
                          </p>
                          <p className="occ-list-item__action">
                            <strong>Ação:</strong> {report.action || "Não informada"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {errorMsg && <div className="occ-list__error">{errorMsg}</div>}

            {reports.length > 0 && (
              <div className="occ-list__footer">
                <span className="occ-list__count">
                  {reports.length} relatório{reports.length !== 1 ? "s" : ""}
                </span>
                <button
                  type="button"
                  className="occ-list__save"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>Salvar Relatórios &rarr;</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {expandedImage && createPortal(
        <div
          className="occ-preview-overlay"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Visualização expandida"
            className="occ-preview-img"
          />
        </div>,
        document.body
      )}

      <AppModal
        isOpen={feedbackModal.open}
        onClose={closeFeedbackModal}
        allowOutsideClick={false}
        variant={feedbackModal.variant}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText={feedbackModal.confirmText}
        cancelText={feedbackModal.cancelText}
        onConfirm={handleFeedbackConfirm}
        onCancel={closeFeedbackModal}
      />
    </>
  );
};

export default ReportCartModal;
